"use strict";
/*
 * Copyright 2024, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.Routing = exports.routingAttributes = exports.routingStatuses = exports.routingModes = void 0;
const Uuidable_1 = require("./Uuidable");
const ConstructorUtils_1 = require("../../utils/ConstructorUtils");
const ParamsValidatorUtils_1 = require("../../utils/ParamsValidatorUtils");
const Result_type_1 = require("../../types/Result.type");
const SurveyObjectUnserializer_1 = require("./SurveyObjectUnserializer");
exports.routingModes = ['walking', 'cycling', 'driving', 'transit'];
exports.routingStatuses = ['success', 'noRoutingFound', 'error'];
// TODO: complete
exports.routingAttributes = [
    '_uuid',
    'endDate',
    'startDate',
    'endTime',
    'startTime',
    'calculatedOn',
    'mode',
    'status',
    'travelTimeS',
    'travelDistanceM'
];
/**
 * A routing is a calculated route for a trip or a segment of trip
 */
class Routing {
    constructor(params, childRoutingAttributes = exports.routingAttributes) {
        params._uuid = Uuidable_1.Uuidable.getUuid(params._uuid);
        this._attributes = {};
        this._customAttributes = {};
        const { attributes, customAttributes } = ConstructorUtils_1.ConstructorUtils.initializeAttributes(params, childRoutingAttributes);
        this._attributes = attributes;
        this._customAttributes = customAttributes;
    }
    get attributes() {
        return this._attributes;
    }
    get customAttributes() {
        return this._customAttributes;
    }
    get _uuid() {
        return this._attributes._uuid;
    }
    get endDate() {
        return this._attributes.endDate;
    }
    set endDate(value) {
        this._attributes.endDate = value;
    }
    get startDate() {
        return this._attributes.startDate;
    }
    set startDate(value) {
        this._attributes.startDate = value;
    }
    get endTime() {
        return this._attributes.endTime;
    }
    set endTime(value) {
        this._attributes.endTime = value;
    }
    get startTime() {
        return this._attributes.startTime;
    }
    set startTime(value) {
        this._attributes.startTime = value;
    }
    get calculatedOn() {
        return this._attributes.calculatedOn;
    }
    set calculatedOn(value) {
        this._attributes.calculatedOn = value;
    }
    get mode() {
        return this._attributes.mode;
    }
    set mode(value) {
        this._attributes.mode = value;
    }
    get status() {
        return this._attributes.status;
    }
    set status(value) {
        this._attributes.status = value;
    }
    get travelTimeS() {
        return this._attributes.travelTimeS;
    }
    set travelTimeS(value) {
        this._attributes.travelTimeS = value;
    }
    get travelDistanceM() {
        return this._attributes.travelDistanceM;
    }
    set travelDistanceM(value) {
        this._attributes.travelDistanceM = value;
    }
    /*get route(): Optional<GENERICROUTE> {
        return this._attributes.route;
    }

    set route(value: Optional<GENERICROUTE>) {
        this._attributes.route = value;
    }*/
    /**
     * Creates a Routing object from sanitized parameters
     * @param {ExtendedRoutingAttributes | SerializedExtendedRoutingAttributes} params - Sanitized routing parameters
     * @param {SurveyObjectsRegistry} _surveyObjectsRegistry - unused since Routing is not composed of other objects, but unserializer will send the optional param.
     * @returns {Routing} New Routing instance
     */
    static unserialize(params, _surveyObjectsRegistry) {
        const flattenedParams = SurveyObjectUnserializer_1.SurveyObjectUnserializer.flattenSerializedData(params);
        return new Routing(flattenedParams);
    }
    /**
     * Factory that validates input from an interview and makes
     * sure types and required fields are valid before returning a new object
     * Custom attributes must be validated in each survey project
     * @param dirtyParams
     * @returns Routing | Error[]
     */
    static create(dirtyParams) {
        const errors = Routing.validateParams(dirtyParams);
        const place = errors.length === 0 ? new Routing(dirtyParams) : undefined;
        if (errors.length > 0) {
            return (0, Result_type_1.createErrors)(errors);
        }
        return (0, Result_type_1.createOk)(place);
    }
    /**
     * Validates attributes types
     * @param dirtyParams The params input
     * @param displayName The name of the object to validate, for error display
     * @returns Error[]
     */
    static validateParams(dirtyParams, displayName = 'Routing') {
        const errors = [];
        // Validate params object:
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isRequired('params', dirtyParams, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isRecord('params', dirtyParams, displayName));
        // Validate _uuid:
        errors.push(...Uuidable_1.Uuidable.validateParams(dirtyParams));
        // Validate endDate:
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isDateString('endDate', dirtyParams.endDate, displayName));
        // Validate startDate:
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isDateString('startDate', dirtyParams.startDate, displayName));
        // Validate endTime:
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isPositiveInteger('endTime', dirtyParams.endTime, displayName));
        // Validate startTime:
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isPositiveInteger('startTime', dirtyParams.startTime, displayName));
        // Validate calculatedOn:
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isPositiveInteger('calculatedOn', dirtyParams.calculatedOn, displayName));
        // Validate mode:
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('mode', dirtyParams.mode, displayName));
        // Validate status:
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('status', dirtyParams.status, displayName));
        // Validate travelTimeS:
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isPositiveInteger('travelTimeS', dirtyParams.travelTimeS, displayName));
        // Validate travelDistanceM:
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isPositiveInteger('travelDistanceM', dirtyParams.travelDistanceM, displayName));
        // Validate params object:
        // TODO: verify all attributes from the route object
        /*errors.push(...ParamsValidatorUtils.isRecord(
            'route',
            dirtyParams.route,
            displayName
        ));*/
        return errors;
    }
}
exports.Routing = Routing;
//# sourceMappingURL=Routing.js.map